import React, { useEffect, useState, useCallback } from "react";
import { DataView } from "primereact/dataview";
import { useLocation, useNavigate } from "react-router-dom";
import { getWarehouseCollection } from "../../services/WalletCollection/WalletCollection";
import { Accordion, AccordionTab } from "primereact/accordion";
import {
  formatDate,
  formatDateNew,
  formatDateNewWithTime,
  localeDateEndDate,
  localeDateStartDate,
  maskMobileNumber,
} from "../../constants/DateUtil/DateUtil";
import StoreMenu from "../StoreMenu";
import SchedularModal from "../SchedularModal";
import {
  getSelectedTabListView,
  getStoreLocalData,
  getUserData,
  setSelectedTabListView,
} from "../../utils/localStorage";
import AdminMenu from "../AdminMenu";
import ButtonLink from "../../ButtonLink";
import useDashboardPaymentCollection from "../StoreContext/useDashboardPaymentCollection";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getBrandStore } from "../../services/StoreUsers/PostStoreUsers";

const { innerHeight: height } = window;

const WalletCollection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { orderFilterData } = location.state || {};

  const { userProfileRole, brandProfileData, storeData } = getUserData();

  const userRole = userProfileRole;
  const brandId = brandProfileData?.id;

  const enabledAmountRoundOff =
    userRole === "BY_STORE" ?
      storeData.enabledAmountRoundOff ? 0 : 2
      : brandProfileData?.enabledAmountRoundOff ? 0 : 2


  const { storeLocalData } = getStoreLocalData();

  const { schedularDate, setSchedularDate } = useDashboardPaymentCollection();

  // --------------------------------------------------
  // State
  // --------------------------------------------------

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [walletCollectionData, setWalletCollectionData] = useState([]);

  const [brandStoreData, setBrandStoreData] = useState([]);

  const [allStores, setAllStores] = useState([]);

  const [selectedStore, setSelectedStore] = useState("ALL");

  const [selectedStoreName, setSelectedStoreName] = useState("ALL")

  const [schedularModalVisible, setSchedularModalVisible] = useState(false);

  const [tabularView, setTabularView] = useState(false);

  const [visible, setVisible] = useState(false);

  const [loading, setLoading] = useState(false);

  // --------------------------------------------------
  // Tab / List View
  // --------------------------------------------------

  const handleTabListViewChange = async (selectedType) => {
    setTabularView(selectedType);

    try {
      await setSelectedTabListView(selectedType);
    } catch (error) {
      console.error(
        "Error saving selected view:",
        error
      );
    }
  };

  const fetchSelectedTabListView = async () => {
    try {
      const selectedInvoiceTypeData = await getSelectedTabListView();

      setTabularView(selectedInvoiceTypeData?.tabListView || "list");
    } catch (error) {
      console.error("Error getting selected view:", error);

      setTabularView("list");
    }
  };

  // --------------------------------------------------
  // Scheduler
  // --------------------------------------------------

  const receiveDataFromSchedularModal = ({ newSchedular }) => {
    setSchedularDate(newSchedular);
  };

  const handleCalendarModal = () => {
    setSchedularModalVisible(true);
  };

  // --------------------------------------------------
  // Get Date Range
  // --------------------------------------------------

  const getDateRange = useCallback(() => {
    const schedulerStartDate = schedularDate?.startDate;

    const schedulerEndDate = schedularDate?.endDate;

    const filterStartDate = orderFilterData?.startDate;

    const filterEndDate = orderFilterData?.endDate;

    const finalStartDate = schedulerStartDate || filterStartDate || startDate;

    const finalEndDate = schedulerEndDate || filterEndDate || endDate;

    return {
      startDate: localeDateStartDate(new Date(finalStartDate)),

      endDate: localeDateEndDate(
        new Date(finalEndDate)
      ),
    };
  }, [
    schedularDate,
    orderFilterData,
    startDate,
    endDate,
  ]);

  // --------------------------------------------------
  // Get Wallet Collection
  // --------------------------------------------------

  const getWalletCollectionData = useCallback(
    async (storeIds) => {
      if (
        !Array.isArray(storeIds) || storeIds.length === 0
      ) {
        setWalletCollectionData([]);
        return;
      }

      try {
        setLoading(true);

        const { startDate, endDate, } = getDateRange();

        const data = {
          storeIds: storeIds,

          statusIn:
            orderFilterData?.statusIn || [],

          startDate: startDate,

          endDate: endDate,
        };

        const response = await getWarehouseCollection(data);

        if (!response?.data?.error) {
          setWalletCollectionData(response?.data || []);
        } else {
          setWalletCollectionData([]);
        }
      } catch (error) {
        console.error(
          "Error fetching wallet collection:",
          error
        );

        setWalletCollectionData([]);
      } finally {
        setLoading(false);
      }
    },
    [
      getDateRange,
      orderFilterData?.statusIn,
    ]
  );

  // --------------------------------------------------
  // Fetch Stores / Initial Data
  // --------------------------------------------------

  useEffect(() => {
    const fetchData = async () => {
      if (!brandId) {
        return;
      }

      try {
        // ==================================================
        // BY_STORE USER
        // ==================================================

        if (userRole === "BY_STORE") {
          const storeIds = Array.isArray(
            orderFilterData?.storeIds
          )
            ? orderFilterData.storeIds
            : [];

          await fetchSelectedTabListView();

          if (storeIds.length > 0) {
            await getWalletCollectionData(
              storeIds
            );
          } else {
            setWalletCollectionData([]);
          }

          return;
        }

        // ==================================================
        // ADMIN / OTHER USERS
        // ==================================================

        const response =
          await getBrandStore(brandId);

        if (response?.data?.error) {
          setBrandStoreData([]);
          setAllStores([]);
          setWalletCollectionData([]);
          return;
        }

        const stores =
          Array.isArray(response?.data)
            ? response.data
            : [];

        setBrandStoreData(stores);

        const storeIds = stores
          .map((item) => item?.id)
          .filter(Boolean);

        setAllStores(storeIds);

        await fetchSelectedTabListView();

        // Initially load ALL stores
        if (storeIds.length > 0) {
          await getWalletCollectionData(
            storeIds
          );
        } else {
          setWalletCollectionData([]);
        }
      } catch (error) {
        console.error(
          "Error fetching stores:",
          error
        );

        setBrandStoreData([]);
        setAllStores([]);
        setWalletCollectionData([]);
      }
    };

    fetchData();
  }, [
    brandId,
    userRole,
    orderFilterData?.storeIds,
    getWalletCollectionData,
  ]);

  // --------------------------------------------------
  // Store Selection / Scheduler Change
  // --------------------------------------------------

  useEffect(() => {
    // BY_STORE users are already handled above.
    // Do not execute ALL/specific store logic for them.
    if (userRole === "BY_STORE") {
      return;
    }

    if (!allStores.length) {
      return;
    }

    let storeIds = [];

    if (selectedStore === "ALL") {
      storeIds = allStores;
    } else {
      storeIds = [selectedStore];
    }

    getWalletCollectionData(storeIds);
  }, [
    selectedStore,
    schedularDate,
    allStores,
    userRole,
    getWalletCollectionData,
  ]);

  const calculateTotalDataCollection = () => {
    if (!Array.isArray(walletCollectionData)) {
      return `${storeLocalData?.currencySymbol || ""} 0`;
    }

    const total = walletCollectionData.reduce((total, item) => {
      return total + (Number(item?.payment?.amount) || 0);
    }, 0);

    return `${storeLocalData?.currencySymbol || ""} ${total.toFixed(enabledAmountRoundOff)}`;
  };

  // --------------------------------------------------
  // Item Template
  // --------------------------------------------------

  const itemTemplate = (order, index) => {
    const orderStatusColor = "#F5C43A";

    const customer =
      order
        ?.storeCustomerAccount
        ?.storeCustomer
        ?.brandCustomer;

    const customerName = `${customer?.firstName || ""} ${customer?.lastName || ""
      }`.trim();

    const mobile = customer?.mobile || "";

    const amount =
      order?.payment?.amount ?? 0;

    const formattedDate =
      order?.payment?.date
        ? formatDate(order.payment.date)
        : "";

    return (
      <div className="flex flex-row">

        {/* Serial Number */}
        <Accordion className="order-accordion">
          <AccordionTab
            style={{
              "--backgroundColor-AccordionTab":
                orderStatusColor,
              fontSize: 10,
            }}
            header={`${index + 1}`}
          />
        </Accordion>

        {/* Order Details */}
        <Accordion className="order-flex-accordion">
          <AccordionTab
            style={{
              "--backgroundColor-AccordionTab":
                orderStatusColor,
            }}
            header={formattedDate}
          >
            <div className="cursor-pointer">
              <div className="flex-row">

                {/* Customer Name */}
                <div className="col-4">
                  <p className="text-base font-semiBold capitalize">
                    Customer Name:
                  </p>

                  <p className="text-lg font-bold capitalize">
                    {customerName || "-"}
                  </p>
                </div>

                {/* Mobile */}
                <div className="col-4">
                  <p className="text-base font-semiBold capitalize">
                    Mobile No.:
                  </p>

                  <p className="text-lg font-bold">
                    {mobile
                      ? maskMobileNumber(mobile)
                      : "-"}
                  </p>
                </div>

                {/* Amount */}
                <div className="col-4">
                  <p className="text-base font-semiBold capitalize">
                    Amount:
                  </p>

                  <p className="text-lg font-bold">
                    {storeLocalData?.currencySymbol || ""}
                    {" "}
                    {amount}
                  </p>
                </div>

              </div>
            </div>
          </AccordionTab>
        </Accordion>

      </div>
    );
  };

  // --------------------------------------------------
  // List Template
  // --------------------------------------------------

  const listTemplate = (items) => {
    if (!items?.length) {
      return null;
    }

    return (
      <div>
        {items.map((item, index) =>
          itemTemplate(item, index)
        )}
      </div>
    );
  };

  // --------------------------------------------------
  // Table View
  // --------------------------------------------------

  const RenderTabView = () => {
    return (
      <DataTable
        showGridlines
        value={walletCollectionData}
        paginator
        rows={50}
        rowsPerPageOptions={[
          50,
          75,
          100,
        ]}
        tableStyle={{
          minWidth: "50rem",
        }}
        stripedRows
        scrollable
        scrollHeight={height - 130}
      >

        {/* S No */}
        <Column
          header="S No."
          style={{
            width: "40px",
          }}
          body={(rowData, options) =>
            options.rowIndex + 1
          }
        />

        {/* Date */}
        <Column
          field="createdDate"
          header="Date"
          style={{
            width: "40px",
            paddingLeft: 5,
            paddingRight: 1,
          }}
          body={(rowData) =>
            rowData?.payment?.date
              ? formatDateNew(
                rowData.payment.date
              )
              : "-"
          }
        />

        {/* Customer Name */}
        <Column
          field="customerName"
          header="Customer Name"
          style={{
            width: "40px",
            paddingLeft: 5,
            paddingRight: 1,
          }}
          body={(rowData) => {
            const customer =
              rowData
                ?.storeCustomerAccount
                ?.storeCustomer
                ?.brandCustomer;

            const name = `${customer?.firstName || ""} ${customer?.lastName || ""
              }`.trim();

            return name || "-";
          }}
        />

        {/* Mobile */}
        <Column
          field="mobile"
          header="Mobile No."
          style={{
            width: "50px",
            paddingLeft: 5,
            paddingRight: 1,
          }}
          body={(rowData) => {
            const mobile =
              rowData
                ?.storeCustomerAccount
                ?.storeCustomer
                ?.brandCustomer
                ?.mobile;

            return mobile
              ? maskMobileNumber(mobile)
              : "-";
          }}
        />

        {/* Amount */}
        <Column
          field="amount"
          header="Amount"
          style={{
            width: "40px",
            paddingLeft: 5,
            paddingRight: 1,
          }}
          body={(rowData) =>
            `${storeLocalData?.currencySymbol || ""
            } ${rowData?.payment?.amount ?? 0
            }`
          }
        />

      </DataTable>
    );
  };

  // --------------------------------------------------
  // Store Buttons
  // --------------------------------------------------

  const renderStoreButtons = () => {
    return brandStoreData?.map((item) => {
      const isSelected =
        item?.id === selectedStore;

      return (
        <div
          key={item?.id}
          style={{
            backgroundColor: isSelected
              ? "green"
              : "#3EB3E1",
          }}
          className="px-3 py-2 border-round-md cursor-pointer"
          onClick={() => {
            setSelectedStore(item?.id);
            setSelectedStoreName(item?.storeName)
          }
          }
        >
          <p className="text-white text-xl font-bold uppercase">
            {(item?.storeName || "")
              .replace(/_/g, " ")}{item?.id === selectedStore && " - " + calculateTotalDataCollection()}
          </p>
        </div>
      );
    });
  };

  // --------------------------------------------------
  // Return
  // --------------------------------------------------


  const exportExcel = () => {

    const filterData = walletCollectionData

    import("xlsx").then((xlsx) => {
      const exportData = filterData.map((item) => ({
        "Date": formatDateNewWithTime(item?.createdDate),
        "Customer Name": item?.storeCustomerAccount?.storeCustomer?.brandCustomer?.firstName + " " + item?.storeCustomerAccount?.storeCustomer?.brandCustomer?.lastName,
        "Mobile No.": item?.storeCustomerAccount?.storeCustomer?.brandCustomer?.mobile,
        "Paid Amount": storeLocalData?.currencySymbol + " " + Number(item.payment.amount)?.toFixed(enabledAmountRoundOff),
      }));



      const worksheet = xlsx.utils.json_to_sheet(exportData);

      const workbook = {
        Sheets: { data: worksheet },
        SheetNames: ["data"],
      };

      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, "Wallet_Collection");
    });
  };

  const saveAsExcelFile = (buffer, fileName) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });

        module.default.saveAs(
          data,
          fileName + "_" + selectedStoreName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
        );
      }
    });
  };

  return (
    <div className="flex-1">

      {/* Header */}
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{
          "--surface-0": "#1752AA",
        }}
      >
        <div className="flex-1 flex ml-3 mr-3">

          {/* Title */}
          <div className="flex-1 flex">

            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">

                <i
                  className="pi pi-angle-left"
                  onClick={() =>
                    navigate("/full-dashboard")
                  }
                  style={{
                    fontSize: "27px",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                />

              </div>
            </div>

            <div className="flex-1 ml-5">
              <p className="text-white text-2xl font-bold uppercase">
                Wallet Collection
              </p>
            </div>

          </div>

          {userRole === "BY_ADMIN" &&
            <div className="flex justify-content-center align-items-center">
              <div className="flex justify-content-center align-items-center cursor-pointer mx-2">
                <img
                  onClick={exportExcel}
                  src={"images/file.png"}
                  style={{
                    width: 30,
                  }}
                />
              </div>
            </div>
          }


          {/* Tab/List Button */}
          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() =>
              handleTabListViewChange(
                tabularView !== "tab"
                  ? "tab"
                  : "list"
              )
            }
          >
            <div
              className="px-3 py-1 cursor-pointer"
              style={{
                backgroundColor: "#55BD5A",
                borderRadius: 5,
                marginRight: 20,
              }}
            >
              <p className="text-lg text-white font-medium capitalize">
                {tabularView !== "tab"
                  ? "Tab View"
                  : "List View"}
              </p>
            </div>
          </div>

          {/* Receipt */}
          {walletCollectionData.length > 0 && (
            <div
              onClick={() =>
                navigate(
                  "/WalletCollectionReceipt",
                  {
                    state: {
                      walletCollectionData,
                      orderFilterData,
                    },
                  }
                )
              }
              className="mx-2 cursor-pointer"
            >
              <img
                src="Laundy_App_Icons_Gifs/Other_Icons/View_Invoice.png"
                style={{
                  width: 30,
                }}
                alt="View Invoice"
              />
            </div>
          )}

          {/* Scheduler */}
          <div
            onClick={handleCalendarModal}
            className="mx-2 cursor-pointer"
          >
            <img
              src="Laundy_App_Icons_Gifs/Other_Icons/schedule.png"
              style={{
                width: 30,
              }}
              alt="Schedule"
            />
          </div>

          {/* Menu */}
          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() => setVisible(true)}
          >
            <i
              className="pi pi-bars"
              style={{
                fontSize: "27px",
                marginRight: 30,
                color: "#fff",
              }}
            />
          </div>

        </div>
      </div>

      {/* Store Filter */}
      {userRole !== "BY_STORE" && (
        <div
          className="flex gap-3 my-3 mx-3 pb-1 new-scroll-bar"
          style={{
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >

          {/* ALL */}
          <div className="ml-2 flex justify-content-between align-items-center">

            <div
              style={{
                backgroundColor:
                  selectedStore === "ALL"
                    ? "green"
                    : "#3EB3E1",
              }}
              className="px-3 py-2 border-round-md cursor-pointer"
              onClick={() =>
                setSelectedStore("ALL")
              }
            >
              <p className="text-white text-xl font-bold uppercase">
                ALL{"ALL" === selectedStore && " - " + calculateTotalDataCollection()}
              </p>
            </div>

          </div>

          {/* Stores */}
          {renderStoreButtons()}

        </div>
      )}

      {/* Content */}
      <div className="mx-4 my-3">

        {loading ? (
          <div>
            <p className="text-2xl font-semibold text-center">
              Wallet Collection is Loading...
            </p>
          </div>
        ) : walletCollectionData.length > 0 ? (

          <div>

            {/* Table View */}
            {tabularView === "tab" && (
              <div className="scroll-bar mb-2">
                <div className="report-table">
                  <div className="tab-view-table">
                    {RenderTabView()}
                  </div>
                </div>
              </div>
            )}

            {/* List View */}
            <div
              style={{
                display:
                  tabularView === "tab"
                    ? "none"
                    : "",
              }}
            >
              <DataView
                value={walletCollectionData}
                listTemplate={listTemplate}
                paginator
                rows={20}
              />
            </div>

          </div>

        ) : (

          <div>
            <p className="text-2xl font-semibold text-center">
              No Data Found
            </p>
          </div>

        )}

      </div>

      {/* Scheduler Modal */}
      <SchedularModal
        setSchedularModalVisible={
          setSchedularModalVisible
        }
        schedularModalVisible={
          schedularModalVisible
        }
        sendDataToOrdersPage={
          receiveDataFromSchedularModal
        }
        typeOfOrder={
          orderFilterData?.recordType
        }
      />

      {/* Menu */}
      {userRole === "BY_ADMIN" ||
        userRole === "BY_SUB_ADMIN_USER" ||
        userRole === "BY_FRANCHISE_ADMIN" ? (

        <AdminMenu
          setVisible={setVisible}
          visible={visible}
        />

      ) : (

        <>
          <StoreMenu
            setVisible={setVisible}
            visible={visible}
          />

          <ButtonLink />
        </>

      )}

    </div>
  );
};

export default WalletCollection;